export const linkifyText = (html: string): string => {
    if (!html) return '';

    // SSR check
    if (typeof DOMParser === 'undefined') return html;

    // Optimization for plain text (to ensure simple links work even if DOMParser has quirks)
    if (!html.includes('<')) {
        const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/g;
        return html.replace(urlRegex, (url) => {
            const href = url.startsWith('www.') ? `https://${url}` : url;
            return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: #3b82f6; pointer-events: auto; cursor: pointer;">${url}</a>`;
        });
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const walk = (node: Node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                // If parent is already a link, skip
                if (node.parentElement?.tagName === 'A') return;

                const text = node.textContent || '';
                // Improved regex to capture http/https and www. urls, excluding common trailing punctuation if possible, but keeping it simple for now
                const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/g;

                const parent = node.parentNode;
                if (!parent) return;

                const fragment = document.createDocumentFragment();
                let lastIndex = 0;
                let match;
                let foundMatch = false;

                // Reset regex state just in case, though new instance per call
                while ((match = urlRegex.exec(text)) !== null) {
                    foundMatch = true;
                    const url = match[0];
                    const index = match.index;

                    // Add text before URL
                    if (index > lastIndex) {
                        fragment.appendChild(document.createTextNode(text.substring(lastIndex, index)));
                    }

                    // Create link element
                    const a = document.createElement('a');
                    // Ensure protocol exists for href
                    a.href = url.startsWith('www.') ? `https://${url}` : url;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    Object.assign(a.style, {
                        textDecoration: 'underline',
                        color: '#3b82f6',
                        pointerEvents: 'auto',
                        cursor: 'pointer'
                    });
                    a.textContent = url;
                    fragment.appendChild(a);

                    lastIndex = index + url.length;
                }

                if (foundMatch) {
                    // Add remaining text
                    if (lastIndex < text.length) {
                        fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
                    }

                    parent.replaceChild(fragment, node);
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                // Skip script, style, and existing links
                const tagName = (node as Element).tagName;
                if (tagName === 'SCRIPT' || tagName === 'STYLE' || tagName === 'A') return;

                // Convert childNodes to array to avoid live collection issues during modification
                const children = Array.from(node.childNodes);
                children.forEach(child => walk(child));
            }
        };

        Array.from(doc.body.childNodes).forEach(walk);

        return doc.body.innerHTML;
    } catch (e) {
        console.error('linkifyText error:', e);
        return html;
    }
};
